import { Immutable } from "immer";
import React from "react";

import type { HeroBlockB as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "../../styles/composable/product-list-hero.module.scss";

type Props = Immutable<{
    value: Value;
}>;

/**
 * React port of the pillows ``HeroBlockB`` (homepage / product-list hero,
 * "b" variant). DOM mirrors the legacy Django template
 * ``cms/components/homepage_hero_b.html`` exactly.
 *
 * Rendered regardless of ``hero_status`` (the ``display-status--{status}``
 * class drives the CSS hide behaviour). The hero image is applied as an
 * inline ``background-image`` only when an image exists. ``hero_headline``/
 * ``hero_subheadline``/``hero_detail_copy`` are RichText;
 * ``hero_detail_headline`` is a plain CharBlock string.
 */
export const HeroBlockB = (props: Props): React.JSX.Element => {
    const v = props.value;
    return (
        <div className={`l-full-width display-status--${v.hero_status ?? ""}`}>
            <div
                className={concatClassNames([
                    "hero",
                    // Homepage CMS raw-HTML <style> targets this literal.
                    "product-list-hero",
                    styles.productListHero,
                    "pillows-hero--b",
                    "l-full-width",
                ])}
                style={
                    v.hero_image
                        ? { backgroundImage: `url(${v.hero_image.url})` }
                        : undefined
                }
            >
                <div
                    className={`pillows-hero__content pillows-hero__content--${v.hero_alignment ?? ""} l-capped-width`}
                >
                    <div
                        className={concatClassNames([
                            styles.productListHeroCopy,
                            "pillows-hero__copy",
                        ])}
                    >
                        <div
                            className={concatClassNames([
                                styles.productListHeroTitle,
                                "pillows-hero__title",
                                "pillows-hero__title--b",
                            ])}
                        >
                            <RichTextBlock value={v.hero_headline ?? ""} />
                        </div>
                        <div className="pillows-hero__subtitle-b">
                            <RichTextBlock value={v.hero_subheadline ?? ""} />
                        </div>
                        <div className="pillows-hero__detail_headline">
                            {v.hero_detail_headline ?? ""}
                        </div>
                        <div className="pillows-hero__detail_copy">
                            <RichTextBlock value={v.hero_detail_copy ?? ""} />
                        </div>
                        <a href={v.hero_link?.url ?? ""}>
                            <div className="button pillows-hero__button button-lochmara-pillows pillows-hero__button--b">
                                {v.hero_link_name ?? ""}
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
